import { CheckCircle, XCircle } from 'lucide-react'
import type { RunExecutionResult } from '../execution-context'
import { CaseDetail } from './case-detail'
import { runtimeFailureFor } from './failure-panel'

interface SubmitResultViewProps {
  result: RunExecutionResult
}

/**
 * Submit mode. All passed: "Accepted — 64/64 testcases passed" with runtime
 * and memory stats, no per-case list. Failure: a single card for the first
 * failing case — the runner stops early, so it is the LAST element of
 * `results`, and summary.passed counts the cases before it.
 */
export function SubmitResultView({ result }: SubmitResultViewProps) {
  const { results, summary, runtimeMs, memoryKb } = result
  const accepted = summary.total > 0 && summary.passed === summary.total

  if (accepted) {
    return (
      <div className="h-full space-y-4 overflow-auto p-4">
        <div className="flex items-center gap-3">
          <CheckCircle className="h-5 w-5 text-green-500" />
          <h2 className="text-lg font-semibold text-green-500">Accepted</h2>
          <span className="text-muted-foreground text-sm">
            — {summary.passed}/{summary.total} testcases passed
          </span>
        </div>

        {(runtimeMs !== null || memoryKb !== null) && (
          <div className="text-muted-foreground flex gap-4 text-sm">
            {runtimeMs !== null && <span>Runtime: {runtimeMs} ms</span>}
            {memoryKb !== null && (
              <span>Memory: {(memoryKb / 1024).toFixed(1)} MB</span>
            )}
          </div>
        )}
      </div>
    )
  }

  // Defensive: on legacy run-all arrays, pick the first failing case.
  // On well-formed stop-early payloads, this selects the single failure.
  // Do not fall back to passing cases.
  const failingCase = results.find((r) => !r.passed) ?? null
  const isRuntimeError =
    failingCase !== null && runtimeFailureFor(failingCase) !== null
  const headerLabel = isRuntimeError ? 'Runtime Error' : 'Wrong Answer'

  return (
    <div className="h-full space-y-4 overflow-auto p-4">
      <div className="flex items-center gap-3">
        <XCircle className="h-5 w-5 text-red-500" />
        <h2 className="text-lg font-semibold text-red-500">{headerLabel}</h2>
        <span className="text-muted-foreground text-sm">
          {summary.passed}/{summary.total} testcases passed
        </span>
      </div>

      {failingCase && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3">
          <CaseDetail result={failingCase} />
        </div>
      )}
    </div>
  )
}
